import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import ReactGA from 'react-ga';
import TagManager from 'react-gtm-module';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { environmentMode } from '../utils/environmentMode';

interface TrackingServiceSettings {
    activeInDevMode: boolean;
    sanitazedNetworkRequests: string[];
    googleTagManager: {
        active: boolean;
        gtmId: string;
    };
    sentry: {
        active: boolean;
        dsn: string;
    };
    logRocket: {
        active: boolean;
        reactPlugin: boolean;
        identifyTracking: boolean;
        dom: {
            textSanitizer: boolean;
            inputSanitizer: boolean;
        };
        workspace: string;
    };
}

/**
 * Allows to use tracking services that helps us debug user behaviour and
 * client-side errors.
 *
 * Currently supported tracking services:
 * - LogRocket
 * - Sentry
 * - Google Tag Manager with Google Analytics
 */
export class TrackingService {
    protected settings: TrackingServiceSettings;

    constructor(settings: TrackingServiceSettings) {
        this.settings = settings;

        const { activeInDevMode, sentry, logRocket, googleTagManager } =
            settings;

        /**
         * Prevent bloating tracking services with all data and errors
         * that happens during development on local server.
         */
        if (!activeInDevMode && environmentMode === 'development') {
            return;
        }

        if (logRocket.active) {
            LogRocket.init(logRocket.workspace, {
                network: {
                    requestSanitizer: (request) =>
                        this.sanitizeUrl(request.url) ? null : request,
                },
                dom: logRocket.dom,
            });

            ReactGA.initialize(googleTagManager.gtmId);

            if (sentry.active) {
                LogRocket.getSessionURL((sessionURL) => {
                    Sentry.configureScope((scope) => {
                        scope.setExtra('sessionURL', sessionURL);
                    });
                });
            }

            if (logRocket.reactPlugin) {
                setupLogRocketReact(LogRocket);
            }

            if (
                logRocket.identifyTracking &&
                sessionStorage.getItem('upscore-request-id')
            ) {
                this.logRocketIdentify(
                    sessionStorage.getItem('upscore-request-id')!,
                );
            }

            LogRocket.getSessionURL((sessionURL) => {
                ReactGA.ga('send', {
                    hitType: 'event',
                    eventCategory: 'LogRocket',
                    eventAction: sessionURL,
                });
            });
        }

        if (sentry.active) {
            Sentry.init({
                dsn: sentry.dsn,
                integrations: [new Integrations.BrowserTracing()],
                tracesSampleRate: 1.0,
                beforeBreadcrumb: (breadcrumb) => {
                    if (breadcrumb.type === 'http') {
                        return this.sanitizeUrl(breadcrumb.data?.url)
                            ? null
                            : breadcrumb;
                    }
                    return breadcrumb;
                },
            });
        }

        if (googleTagManager.active) {
            TagManager.initialize({
                gtmId: googleTagManager.gtmId,
            });
        }
    }

    public logRocketIdentify = (key: string) => LogRocket.identify(key);

    protected sanitizeUrl = (url: string) => {
        const { sanitazedNetworkRequests } = this.settings;

        if (typeof url !== 'string') return false;

        return sanitazedNetworkRequests.find((sanitazedUrl) =>
            url.toLowerCase().includes(sanitazedUrl),
        )
            ? true
            : false;
    };
}
